import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography, Button, Chip, Divider,
  Breadcrumbs, Link, CircularProgress, Alert, Tooltip, LinearProgress, List,
  IconButton, TextField, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassIcon,
  School as SchoolIcon,
  Category as CategoryIcon,
  Book as BookIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { readingPracticeRequestsService } from '../../../../services/request-service';
import MarginBottomContainer from '../../../../components/containers/MarginBottomContainer';

const PracticeCollectionRequestDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editingItemId, setEditingItemId] = useState(null);
  const [editingTopic, setEditingTopic] = useState('');
  const [editingPlot, setEditingPlot] = useState('');
  const [editingConnection, setEditingConnection] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  const [deleteConfirmItemId, setDeleteConfirmItemId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [deleteRequestOpen, setDeleteRequestOpen] = useState(false);
  const [deleteRequestLoading, setDeleteRequestLoading] = useState(false);

  const [insertingAtIndex, setInsertingAtIndex] = useState(null);
  const [insertingTopic, setInsertingTopic] = useState('');
  const [insertLoading, setInsertLoading] = useState(false);

  const handleStartInsert = (index) => {
    setInsertingAtIndex(index);
    setInsertingTopic('');
  };

  const handleCancelInsert = () => {
    setInsertingAtIndex(null);
    setInsertingTopic('');
  };

  const handleSaveInsert = async (index) => {
    if (!insertingTopic.trim()) return;
    setInsertLoading(true);
    try {
      const updated = await readingPracticeRequestsService.addCollectionItem(id, index, insertingTopic.trim());
      setRequest(updated);
      localStorage.removeItem('swr_cache_practice-collections');
      toast.success('New story added successfully');
      setInsertingAtIndex(null);
      setInsertingTopic('');
    } catch (err) {
      toast.error(`Addition failed: ${err.message}`);
    } finally {
      setInsertLoading(false);
    }
  };

  const handleStartEdit = (item) => {
    setEditingItemId(item.itemId);
    setEditingTopic(item.topic || '');
    setEditingPlot(item.plot || '');
    setEditingConnection(item.connectionToSource || '');
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditingTopic('');
    setEditingPlot('');
    setEditingConnection('');
  };

  const handleSaveEdit = async () => {
    if (!editingItemId || !editingTopic.trim()) return;
    setEditLoading(true);
    try {
      const updated = await readingPracticeRequestsService.editCollectionItem(
        id, 
        editingItemId, 
        editingTopic.trim(),
        editingPlot.trim() || null,
        editingConnection.trim() || null
      );
      setRequest(updated);
      localStorage.removeItem('swr_cache_practice-collections');
      toast.success('Story details updated successfully');
      setEditingItemId(null);
      setEditingTopic('');
      setEditingPlot('');
      setEditingConnection('');
    } catch (err) {
      toast.error(`Update failed: ${err.message}`);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteClick = (itemId) => {
    setDeleteConfirmItemId(itemId);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmItemId(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmItemId) return;
    setDeleteLoading(true);
    try {
      const updated = await readingPracticeRequestsService.deleteCollectionItem(id, deleteConfirmItemId);
      setRequest(updated);
      localStorage.removeItem('swr_cache_practice-collections');
      toast.success('Item deleted successfully');
      setDeleteConfirmItemId(null);
    } catch (err) {
      toast.error(`Deletion failed: ${err.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleConfirmDeleteRequest = async () => {
    setDeleteRequestLoading(true);
    try {
      await readingPracticeRequestsService.deletePracticeCollectionRequest(id);
      localStorage.removeItem('swr_cache_practice-collections');
      toast.success('Collection request deleted successfully');
      setDeleteRequestOpen(false);
      navigate('/practice-collections');
    } catch (err) {
      toast.error(`Deletion failed: ${err.message}`);
      setDeleteRequestLoading(false);
    }
  };

  const fetchDetails = useCallback(async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const data = await readingPracticeRequestsService.getPracticeCollectionRequestById(id);
      setRequest(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching request details:', err);
      setError(err.message || 'Failed to load request details');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetails(true);
  }, [fetchDetails]);

  // Polling logic
  useEffect(() => {
    if (!request) return;

    // Let's poll if request status is transitional or if any item is GENERATING
    const activeGeneration = 
      request.status === 'PENDING' ||
      request.status === 'GENERATING_OUTLINE' ||
      request.status === 'CREATING_COLLECTION' ||
      request.items.some(item => item.status === 'GENERATING');

    if (!activeGeneration) return;

    const interval = setInterval(() => {
      fetchDetails(false);
    }, 5000);

    return () => clearInterval(interval);
  }, [request, fetchDetails]);

  const handleGenerateItem = async (itemId) => {
    const index = request.items.findIndex(item => item.itemId === itemId);
    if (index === -1) return;
    const item = request.items[index];
    const canFire = item.status === 'PENDING' || item.status === 'FAILED';

    if (!canFire) {
      toast.error('Cannot start generation. Story is not in a generateable state (must be PENDING or FAILED).');
      return;
    }

    // Optimistic UI update to GENERATING status
    setRequest(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map(item => 
          item.itemId === itemId ? { ...item, status: 'GENERATING' } : item
        )
      };
    });

    try {
      const updated = await readingPracticeRequestsService.generateCollectionItem(id, itemId);
      setRequest(updated);
      toast.success('Practice generation started!');
    } catch (err) {
      toast.error(`Generation failed: ${err.message}`);
      fetchDetails(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (error) {
    return (
      <MarginBottomContainer>
        <Box sx={{ mt: 3 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            onClick={() => navigate('/practice-collections')}
          >
            Back to Collections
          </Button>
        </Box>
      </MarginBottomContainer>
    );
  }

  if (!request) return null;

  // Compute status helpers
  const totalItems = request.items.length;
  const generatedItems = request.items.filter(item => item.status === 'GENERATED').length;
  const progressPercent = totalItems > 0 ? (generatedItems / totalItems) * 100 : 0;
  const allCompleted = request.status === 'COMPLETED';
  const lastGeneratedIndex = request.items ? request.items.reduce((lastIdx, it, idx) => {
    return it.status === 'GENERATED' ? idx : lastIdx;
  }, -1) : -1;
  const isAnyItemGenerating = request.items ? request.items.some(it => it.status === 'GENERATING') : false;

  // Request status badge styling
  const getRequestStatusChip = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <Chip label="Completed" color="success" size="small" />;
      case 'FAILED':
        return <Chip label="Failed" color="error" size="small" />;
      case 'PENDING':
        return <Chip label="Pending" color="warning" size="small" />;
      case 'GENERATING_OUTLINE':
        return <Chip label="Generating Outline" color="info" size="small" icon={<CircularProgress size={14} color="inherit" />} />;
      case 'GENERATING_PRACTICES':
        return <Chip label="Generating Practices" color="info" size="small" />;
      case 'CREATING_COLLECTION':
        return <Chip label="Assembling Collection" color="info" size="small" icon={<CircularProgress size={14} color="inherit" />} />;
      default:
        return <Chip label={status} variant="outlined" size="small" />;
    }
  };

  const getTooltipTitle = (item, index, isAnyItemGenerating) => {
    if (item.status === 'GENERATED') return '';
    if (item.status === 'GENERATING') return 'Generating story...';
    return item.status === 'FAILED' ? 'Regenerate this item' : 'Generate this item';
  };

  // Item status rendering
  const getItemStatusElement = (item, index) => {
    const canFire = item.status === 'PENDING' || item.status === 'FAILED';

    const tooltipTitle = getTooltipTitle(item, index, isAnyItemGenerating);

    switch (item.status) {
      case 'GENERATED':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" />
            <Typography variant="body2" color="success.main" fontWeight="600" sx={{ mr: 0.5 }}>Generated</Typography>
            {item.practiceId && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => navigate(`/reading-practices/draft/${item.practiceId}`)}
                sx={{ py: 0.5, px: 1.5 }}
              >
                View
              </Button>
            )}
          </Box>
        );
      case 'GENERATING':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="primary.main" fontWeight="600">Generating...</Typography>
          </Box>
        );
      case 'FAILED':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon color="error" />
            <Typography variant="body2" color="error.main" fontWeight="600">Failed</Typography>
            <Tooltip title={tooltipTitle}>
              <span>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => handleGenerateItem(item.itemId)}
                  disabled={!canFire}
                  sx={{ ml: 1, py: 0.5, px: 1.5 }}
                >
                  Retry
                </Button>
              </span>
            </Tooltip>
          </Box>
        );
      case 'PENDING':
      default:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HourglassIcon color="action" />
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Ready</Typography>
            <Tooltip title={tooltipTitle}>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => handleGenerateItem(item.itemId)}
                  disabled={!canFire}
                  sx={{ py: 0.5, px: 1.5 }}
                >
                  Generate
                </Button>
              </span>
            </Tooltip>
          </Box>
        );
    }
  };

  const renderInsertDivider = (insertIndex) => {
    if (request.status === 'COMPLETED' || isAnyItemGenerating) return null;
    
    return (
      <Box sx={{ my: 1, position: 'relative' }}>
        {insertingAtIndex === insertIndex ? (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              py: 1.5, 
              px: 3, 
              border: '1px dashed',
              borderColor: 'primary.main',
              borderRadius: 2,
              backgroundColor: 'action.hover'
            }}
          >
            <TextField
              placeholder="Enter new story topic..."
              value={insertingTopic}
              onChange={(e) => setInsertingTopic(e.target.value)}
              size="small"
              fullWidth
              autoFocus
              disabled={insertLoading}
              sx={{ 
                '& .MuiInputBase-input': { fontWeight: '600', py: 0.5 }
              }}
            />
            {insertLoading ? (
              <CircularProgress size={24} />
            ) : (
              <>
                <IconButton
                  color="success"
                  size="small"
                  onClick={() => handleSaveInsert(insertIndex)}
                  disabled={!insertingTopic.trim()}
                >
                  <CheckIcon />
                </IconButton>
                <IconButton
                  color="error"
                  size="small"
                  onClick={handleCancelInsert}
                >
                  <CloseIcon />
                </IconButton>
              </>
            )}
          </Box>
        ) : (
          <Box
            onClick={() => handleStartInsert(insertIndex)}
            sx={{
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              opacity: 0,
              transition: 'opacity 0.2s ease, margin 0.2s ease',
              my: -1.5,
              zIndex: 2,
              position: 'relative',
              '&:hover': {
                opacity: 1,
                my: 0.5,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                height: '2px',
                backgroundColor: 'primary.light',
              }
            }}
          >
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<AddIcon fontSize="small" />}
              sx={{
                py: 0.25,
                px: 1.5,
                fontSize: '0.75rem',
                borderRadius: 4,
                boxShadow: 2,
                height: 22,
                minWidth: 0,
                textTransform: 'none',
                '& .MuiButton-startIcon': { mr: 0.5, ml: -0.25 }
              }}
            >
              Add Story
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <MarginBottomContainer>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            color="inherit"
            onClick={() => navigate('/practice-collections')}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', '&:hover': { textDecoration: 'underline' } }}
          >
            <ArrowBackIcon fontSize="small" sx={{ mr: 0.5 }} />
            Collections
          </Link>
          <Typography color="text.primary">Request Details</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="700">
              Practice Collection Request
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              ID: {id}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {!allCompleted && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteRequestOpen(true)}
              >
                Delete Request
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => fetchDetails(true)}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Main Grid */}
      <Grid container spacing={4}>
        {/* Left Side: Progress & Items */}
        <Grid item xs={12} md={8}>
          {allCompleted && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 4, 
                borderRadius: 2, 
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.1)',
                '& .MuiAlert-message': { width: '100%' } 
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="700">
                    Collection Draft Compiled!
                  </Typography>
                  <Typography variant="body2">
                    All items have been generated. You can now view and edit the compiled collection draft.
                  </Typography>
                </Box>
                <Button 
                  variant="contained" 
                  color="success" 
                  onClick={() => navigate('/practice-collections')}
                  sx={{ boxShadow: 'none' }}
                >
                  View Collections
                </Button>
              </Box>
            </Alert>
          )}

          <Card sx={{ mb: 4, border: '1px solid', borderColor: 'divider', overflow: 'visible' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="600">
                  Generation Progress
                </Typography>
                <Typography variant="body2" fontWeight="600" color="text.secondary">
                  {generatedItems} / {totalItems} completed ({Math.round(progressPercent)}%)
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={progressPercent} 
                sx={{ height: 10, borderRadius: 5, mb: 4, backgroundColor: 'divider' }}
              />

              <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                Practice Stories Outline
              </Typography>
              
              <List disablePadding>
                {request.items.map((item, index) => (
                  <Box key={item.itemId}>
                    {index > lastGeneratedIndex && renderInsertDivider(index)}
                    <Box 
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 2.5,
                        px: 3,
                        borderRadius: 2,
                        mb: index < request.items.length - 1 ? 2 : 0,
                        border: '1px solid',
                        borderColor: item.status === 'GENERATING' ? 'primary.main' : 'divider',
                        backgroundColor: item.status === 'GENERATING' ? 'action.hover' : 'background.paper',
                        transition: 'all 0.2s ease',
                        boxShadow: 'none',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: (theme) => theme.palette.mode === 'dark'
                            ? '0 6px 20px rgba(0,0,0,0.3)'
                            : '0 6px 20px rgba(0,0,0,0.05)',
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, mr: 2 }}>
                        <Box 
                          sx={{ 
                            width: 32, 
                            height: 32, 
                            borderRadius: '50%', 
                            backgroundColor: item.status === 'GENERATED' ? 'success.light' : 'action.disabledBackground', 
                            color: item.status === 'GENERATED' ? 'success.contrastText' : 'text.secondary',
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            fontWeight: '700',
                            fontSize: '0.875rem'
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              {item.status === 'GENERATED' && item.practiceId ? (
                                <Typography
                                  variant="body1"
                                  fontWeight="600"
                                  onClick={() => navigate(`/reading-practices/draft/${item.practiceId}`)}
                                  sx={{
                                    wordBreak: 'break-word',
                                    cursor: 'pointer',
                                    color: 'primary.main',
                                    '&:hover': { textDecoration: 'underline' }
                                  }}
                                >
                                  {item.topic}
                                </Typography>
                              ) : (
                                <Typography variant="body1" fontWeight="600" sx={{ wordBreak: 'break-word' }}>
                                  {item.topic}
                                </Typography>
                              )}
                              
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, mb: 0.5 }}>
                                {item.genre && (
                                  <Chip
                                    label={item.genre === 'FICTION' ? 'Fiction' : 'Non-Fiction'}
                                    size="small"
                                    sx={{
                                      backgroundColor: item.genre === 'FICTION' ? 'secondary.light' : 'info.light',
                                      color: item.genre === 'FICTION' ? 'secondary.dark' : 'info.dark',
                                      fontWeight: '600',
                                      fontSize: '0.75rem',
                                      height: 20
                                    }}
                                  />
                                )}
                              </Box>

                              {item.plot && (
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  sx={{ mt: 1.5, wordBreak: 'break-word', fontSize: '0.825rem', lineHeight: 1.4 }}
                                >
                                  <strong>Plot:</strong> {item.plot}
                                </Typography>
                              )}
                              {item.connectionToSource && (
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  sx={{ mt: 0.5, wordBreak: 'break-word', fontSize: '0.825rem', lineHeight: 1.4 }}
                                >
                                  <strong>Connection:</strong> {item.connectionToSource}
                                </Typography>
                              )}

                              {item.failureReason && (
                                <Typography variant="caption" color="error.main" sx={{ mt: 0.5, display: 'block', wordBreak: 'break-word' }}>
                                  Error: {item.failureReason}
                                </Typography>
                              )}
                            </Box>
                            {request.status !== 'COMPLETED' && !isAnyItemGenerating && (lastGeneratedIndex === -1 || index >= lastGeneratedIndex) && (
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {item.status !== 'GENERATED' && (
                                  <Tooltip title="Edit Story Details">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleStartEdit(item)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Delete Item">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteClick(item.itemId)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{ minWidth: 140, display: 'flex', justifyContent: 'flex-end' }}>
                        {getItemStatusElement(item, index)}
                      </Box>
                    </Box>
                  </Box>
                ))}
                {request.items.length > lastGeneratedIndex && renderInsertDivider(request.items.length)}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Side: Metadata / Configuration */}
        <Grid item xs={12} md={4}>
          <Card sx={{ border: '1px solid', borderColor: 'divider', mb: 4 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" fontWeight="600" sx={{ mb: 3 }}>
                Request Details
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Status
                  </Typography>
                  {getRequestStatusChip(request.status)}
                </Box>

                <Divider />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <SchoolIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      School Level
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {request.schoolLevel?.replace('_', ' ') || 'Not Specified'}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <BookIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Type
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {request.type?.replace('_', ' ') || 'Fiction'}
                    </Typography>
                  </Box>
                </Box>

                {request.categoryId && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CategoryIcon color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Category
                      </Typography>
                      <Typography variant="body2" fontWeight="600">
                        {request.categoryId}
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CalendarIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Submitted At
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {new Date(request.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Source Material Card */}
          <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
                Source Material / Topic
              </Typography>
              <Box 
                sx={{ 
                  maxHeight: 250, 
                  overflowY: 'auto', 
                  backgroundColor: 'background.subtle', 
                  p: 2, 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: '0.875rem',
                  color: 'text.secondary'
                }}
              >
                {request.sourceMaterial || 'No source material provided.'}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmItemId !== null}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {"Delete Outline Item?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this story outline item? If this item has already been generated, the corresponding reading practice draft will also be deleted. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelDelete} color="inherit" disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained" 
            autoFocus 
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={24} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Story Details Dialog */}
      <Dialog
        open={editingItemId !== null}
        onClose={handleCancelEdit}
        aria-labelledby="edit-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="edit-dialog-title" fontWeight="600">
          Edit Story Details
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <TextField
              label="Topic / Title"
              value={editingTopic}
              onChange={(e) => setEditingTopic(e.target.value)}
              fullWidth
              disabled={editLoading}
              required
            />
            <TextField
              label="Plot Summary"
              value={editingPlot}
              onChange={(e) => setEditingPlot(e.target.value)}
              fullWidth
              multiline
              rows={4}
              disabled={editLoading}
              placeholder="Enter plot plan for this story..."
            />
            <TextField
              label="Connection to Source Material"
              value={editingConnection}
              onChange={(e) => setEditingConnection(e.target.value)}
              fullWidth
              multiline
              rows={3}
              disabled={editLoading}
              placeholder="Explain how this story connects to the source material..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCancelEdit} color="inherit" disabled={editLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            color="primary" 
            variant="contained" 
            disabled={editLoading || !editingTopic.trim()}
          >
            {editLoading ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Request Confirmation Dialog */}
      <Dialog
        open={deleteRequestOpen}
        onClose={() => setDeleteRequestOpen(false)}
        aria-labelledby="delete-request-dialog-title"
        aria-describedby="delete-request-dialog-description"
      >
        <DialogTitle id="delete-request-dialog-title">
          {"Delete Collection Request?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-request-dialog-description">
            Are you sure you want to delete this practice collection request? This will permanently delete the request and all generated reading practice drafts associated with it. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteRequestOpen(false)} color="inherit" disabled={deleteRequestLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDeleteRequest} 
            color="error" 
            variant="contained" 
            autoFocus 
            disabled={deleteRequestLoading}
          >
            {deleteRequestLoading ? <CircularProgress size={24} color="inherit" /> : "Delete Request"}
          </Button>
        </DialogActions>
      </Dialog>
    </MarginBottomContainer>
  );
};

export default PracticeCollectionRequestDetails;
